import type { AuthUser } from '@/types';

export const DEFAULT_BRANCH = 'jagatpura';

const BRANCH_COLLECTION_KEYS = [
  'branches',
  'accessibleBranches',
  'accessible_branches',
  'branchOptions',
  'branch_options',
] as const;

function normalize(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toBranchName(value: unknown): string {
  if (typeof value === 'string') return normalize(value);
  if (!value || typeof value !== 'object') return '';

  const record = value as Record<string, unknown>;
  for (const key of ['name', 'label', 'branch', 'branch_name', 'code', 'value']) {
    const branchName = normalize(record[key]);
    if (branchName) return branchName;
  }

  return '';
}

export function normalizeBranchName(value: unknown): string {
  return toBranchName(value);
}

export function formatBranchLabel(value: string): string {
  const branch = normalize(value);
  if (!branch) return 'No branch selected';

  return branch
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => {
      if (segment.length <= 3 && segment === segment.toUpperCase()) {
        return segment;
      }

      return segment.charAt(0).toUpperCase() + segment.slice(1);
    })
    .join(' ');
}

export function isAdminRole(role?: string | null): boolean {
  const r = normalize(role).toLowerCase().replace(/[^a-z_]/g, '');
  return r === 'super_admin' || r === 'admin';
}

export function isSuperAdminRole(role?: string | null): boolean {
  return normalize(role).toLowerCase().replace(/[^a-z_]/g, '') === 'super_admin';
}

export function getAvailableBranches(user: AuthUser | null): string[] {
  if (!user) return [];

  const rawUser = user as AuthUser & Record<string, unknown>;
  const uniqueBranches = new Map<string, string>();

  [user.branch].forEach((entry) => {
    const branch = toBranchName(entry);
    if (branch) uniqueBranches.set(branch.toLowerCase(), branch);
  });

  for (const key of BRANCH_COLLECTION_KEYS) {
    const value = rawUser[key];
    if (!Array.isArray(value)) continue;

    value.forEach((entry) => {
      const branch = toBranchName(entry);
      if (branch) uniqueBranches.set(branch.toLowerCase(), branch);
    });
  }

  return uniqueBranches.size > 0 ? [...uniqueBranches.values()] : [DEFAULT_BRANCH];
}